import {useEffect, useState} from 'react';
import {Box, Link, Typography, alpha} from '@mui/material';
import Avatar from '@jobseeker/lotus/components/Avatar';
import {Image} from '@jobseeker/lotus/components/Image';
import {Tooltip} from '@jobseeker/lotus/elements';
import {
  LockPrem,
  GreenArrowRight,
  ConfiTag,
  VerifiedRecruiter,
} from '@jobseeker/lotus/assets/icons';
import {useIsCustomWidth} from '@jobseeker/lotus/hooks';
import {HiristQr, JdInsightsQr} from '@jobseeker/lotus/assets/images';
import {
  getEvaluationAppLink,
  getScannerAppLink,
  accessEvaluationInApp,
} from './appLink';

type Props = {
  jobId: string | number;
  companyName?: string;
  recruiterDesignation?: string;
  recruiterImgUrl?: string;
  isAppHirist: boolean;
  showInsights?: boolean;
  openInsightsDialog?: () => void;
  disableAppDownloadCta?: boolean;
  confidential?: boolean;
  setOpenConfJobDialog?: (open: boolean) => void;
  onLockedInsightClick?: (section: string) => void;
};

type RelatedApp = {platform: string; id?: string};
type AppNavigator = Navigator & {
  getInstalledRelatedApps?: () => Promise<RelatedApp[]>;
};

export default function EvaluationGate({
  jobId,
  companyName,
  recruiterDesignation,
  recruiterImgUrl,
  isAppHirist,
  showInsights = false,
  openInsightsDialog,
  disableAppDownloadCta = false,
  confidential = false,
  setOpenConfJobDialog,
  onLockedInsightClick,
}: Props) {
  const isSmall = useIsCustomWidth('sm');
  const appName = isAppHirist ? 'Hirist app' : 'iimjobs app';
  const hiringCompanyName = companyName?.trim() || 'the hiring company';
  const normalizedDesignation = recruiterDesignation?.trim();
  const visibleDesignation =
    normalizedDesignation && !/^[*•]+$/.test(normalizedDesignation)
      ? normalizedDesignation
      : undefined;
  const appLink = getEvaluationAppLink(jobId, isAppHirist);
  const scannerLink = getScannerAppLink(isAppHirist);
  const [canOpenApp, setCanOpenApp] = useState(false);

  useEffect(() => {
    let active = true;
    setCanOpenApp(false);
    const detectApp = async () => {
      try {
        const apps = await (
          navigator as AppNavigator
        ).getInstalledRelatedApps?.();
        const packageId = isAppHirist
          ? 'com.hirist.jobseeker'
          : 'com.org.iimjobs';
        if (active) {
          setCanOpenApp(
            !!apps?.some(app => app.platform === 'play' && app.id === packageId)
          );
        }
      } catch {
        // Detection is optional. The same OneLink resolves the app/store either way.
      }
    };
    void detectApp();
    return () => {
      active = false;
    };
  }, [isAppHirist]);

  return (
    <Box
      data-testid="evaluation-gate"
      data-evaluation-gate
      sx={{backgroundColor: '#FFFFFF', color: '#292929'}}
    >
      <Typography
        color="#7E7E7E"
        fontSize={{xxs: 12, md: 14}}
        lineHeight="20px"
      >
        Posted by
      </Typography>
      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
        <Avatar
          imgUrl={recruiterImgUrl}
          sx={{
            width: {xxs: 36, md: 44},
            height: {xxs: 36, md: 44},
            border: '1px solid',
            borderColor: 'primary.main',
            flexShrink: 0,
            backgroundColor: 'white',
            ...(confidential
              ? {border: '1px solid #7E7E7E33', color: '#7E7E7E33'}
              : {}),
          }}
        />
        <Box minWidth={0}>
          {confidential ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: 0.5,
              }}
            >
              <Typography
                component="p"
                lineHeight={{
                  xxs: isAppHirist ? '24px' : '16px',
                  md: isAppHirist ? '24px' : '20px',
                }}
                color="text.primary"
                variant="subtitle2"
                fontSize={{
                  xxs: isAppHirist ? '18px' : '10px',
                  md: isAppHirist ? '18px' : '14px',
                }}
                fontWeight={isAppHirist ? 600 : 500}
                sx={theme => ({
                  background: isAppHirist
                    ? `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #FFAD02 98.08%)`
                    : 'linear-gradient(90deg, #225448 0%, #7E9F67 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                })}
              >
                Verified Recruiter
              </Typography>
              <Tooltip
                id="Verified_Recruiter_tag"
                data-testid="Verified_Recruiter_tag"
                title={
                  <Typography variant="body2">
                    This is a confidential job posted by a Verified Recruiter.
                    While the company name is hidden, the role has been verified
                    by {!isAppHirist ? 'iimjobs.com' : 'hirist.tech'}.{' '}
                    <span
                      style={{
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setOpenConfJobDialog?.(true);
                      }}
                    >
                      Learn More
                    </span>
                  </Typography>
                }
                arrow
              >
                <Image
                  className="verified_recruiter_tag"
                  src={isAppHirist ? VerifiedRecruiter : ConfiTag}
                  alt={'premium_icon'}
                  width={isSmall ? 12 : 17}
                  height={isSmall ? 12 : 17}
                  style={{
                    objectFit: 'cover',
                  }}
                />
              </Tooltip>
            </Box>
          ) : (
            <Box
              role="img"
              aria-label="Recruiter name available in the app"
              display="flex"
              alignItems="center"
              gap={0.25}
              minHeight={18}
              onClick={() => onLockedInsightClick?.('recruiterName')}
              sx={{cursor: 'pointer'}}
            >
              <Typography
                component="span"
                aria-hidden="true"
                fontSize={{xxs: 13, md: 14}}
                fontWeight={700}
                lineHeight="18px"
                letterSpacing="0.5px"
              >
                •••••
              </Typography>
              <Image
                src={LockPrem}
                alt=""
                aria-hidden="true"
                width={11}
                height={11}
                style={{flexShrink: 0}}
              />
            </Box>
          )}
          {!confidential &&
            (visibleDesignation ? (
              <Typography
                fontSize={{xxs: 12, md: 14}}
                fontWeight={500}
                lineHeight="18px"
              >
                {visibleDesignation} at {hiringCompanyName}
              </Typography>
            ) : (
              <Box
                role="img"
                aria-label={`Recruiter designation available in the app at ${hiringCompanyName}`}
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                columnGap={0.25}
                minHeight={18}
              >
                <Typography
                  component="span"
                  aria-hidden="true"
                  fontSize={{xxs: 13, md: 14}}
                  fontWeight={700}
                  lineHeight="18px"
                  letterSpacing="0.5px"
                >
                  •••••
                </Typography>
                <Image
                  src={LockPrem}
                  alt=""
                  aria-hidden="true"
                  width={11}
                  height={11}
                  style={{flexShrink: 0}}
                />
                <Typography
                  component="span"
                  aria-hidden="true"
                  fontSize={{xxs: 12, md: 14}}
                  fontWeight={500}
                  lineHeight="18px"
                >
                  at {hiringCompanyName}
                </Typography>
              </Box>
            ))}
        </Box>
      </Box>

      <Box bgcolor="#F6F7F8" borderRadius="16px" mt={2} p={1}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(3, minmax(0, 1fr))"
          gap={1}
          sx={{
            px: 0.5,
            pt: 1,
            pb: 1.5,
          }}
        >
          {['Job Views', 'Applications', 'Recruiter Actions'].map(label => (
            <Box
              key={label}
              display="flex"
              flexDirection="column"
              onClick={() => onLockedInsightClick?.('JdInsightLock')}
              sx={{textAlign: 'center', cursor: 'pointer'}}
            >
              <Box
                component="span"
                role="img"
                aria-label="Available in the app"
                sx={{
                  order: {xxs: 2, md: 0},
                  minHeight: {xxs: 14, md: 28},
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    display: 'inline-block',
                    color: '#292929',
                    fontSize: 15,
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    lineHeight: '14px',
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                >
                  •••
                </Box>
              </Box>
              <Typography
                fontSize={{xxs: 10, md: 11}}
                lineHeight="15px"
                color="text.secondary"
                sx={{textTransform: {xxs: 'none', md: 'uppercase'}}}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {!disableAppDownloadCta && (
          <>
            <Link
              href={appLink}
              variant="body2"
              underline="none"
              onClick={() => onLockedInsightClick?.('AppDwnloadJdInsight')}
              sx={theme => ({
                display: {xxs: 'flex', md: 'none'},
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.6),
                borderRadius: '6px',
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                boxShadow: '0 2px 4px rgba(41, 41, 41, 0.08)',
                minHeight: 40,
                boxSizing: 'border-box',
                px: 1,
                py: 0.5,
                textAlign: 'left',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              })}
            >
              <Image
                src={LockPrem}
                alt=""
                aria-hidden="true"
                width={17}
                height={17}
                style={{flexShrink: 0, paddingRight: 4, marginLeft: 7}}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '215px',
                  ml: 1,
                }}
              >
                <Box
                  component="span"
                  flex={1}
                  sx={{
                    fontFamily: isAppHirist ? 'Nunito Sans' : 'Poppins',
                    fontWeight: 600,
                    fontSize: '11px',
                    lineHeight: '15px',
                    letterSpacing: 0,
                    marginRight: '15px',
                  }}
                >
                  {canOpenApp
                    ? 'Open in app'
                    : isAppHirist
                    ? 'Download Hirist app'
                    : 'Download App'}{' '}
                  to access recruiter details and insights
                </Box>
                <Image
                  src={GreenArrowRight}
                  alt=""
                  aria-hidden="true"
                  width={13}
                  height={13}
                  style={{flexShrink: 0, paddingLeft: 4}}
                />
              </Box>
            </Link>

            <Link
              href={scannerLink}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              onClick={() => onLockedInsightClick?.('ScanInsight')}
              sx={theme => ({
                display: {xxs: 'none', md: 'block'},
                color: 'inherit',
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.75),
                borderRadius: '12px',
                background: `linear-gradient(110deg, #fff 0%, ${alpha(
                  theme.palette.primary.main,
                  0.045
                )} 100%)`,
                p: 1.25,
                cursor: 'pointer',
              })}
            >
              <Box display="flex" alignItems="flex-start" gap={0.5} mb={1}>
                <Image
                  src={LockPrem}
                  alt=""
                  aria-hidden="true"
                  width={15}
                  height={15}
                  style={{flexShrink: 0, marginTop: 1}}
                />
                <Typography
                  component="h2"
                  fontSize={14}
                  fontWeight={600}
                  lineHeight="19px"
                >
                  Scan to unlock these Insights
                </Typography>
              </Box>
              <Box display="flex" alignItems="flex-start" gap={1.25}>
                <Box
                  sx={theme => ({
                    flexShrink: 0,
                    lineHeight: 0,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.45),
                    borderRadius: '6px',
                    overflow: 'hidden',
                    backgroundColor: 'white',
                  })}
                >
                  <Image
                    src={isAppHirist ? HiristQr : JdInsightsQr}
                    alt={`Scan to view this job in the ${appName}`}
                    width={80}
                    height={80}
                  />
                </Box>
                <Typography
                  fontSize={12}
                  lineHeight="18px"
                  color="text.primary"
                >
                  Recruiter details and job stats are available on the {appName}
                  .
                </Typography>
              </Box>
            </Link>
          </>
        )}

        {showInsights && !isAppHirist && (
          <Box
            sx={{
              display: {xxs: 'none', md: 'block'},
              bgcolor: 'white',
              borderRadius: '8px',
              p: 1.25,
              mt: 1.5,
              mb: 0.5,
            }}
          >
            <Typography fontSize={14} lineHeight="20px">
              See how you stand against competition
            </Typography>
            <Link
              component="button"
              type="button"
              underline="none"
              variant="body2"
              onClick={
                openInsightsDialog ??
                (() => accessEvaluationInApp(jobId, isAppHirist, false))
              }
              sx={{
                fontSize: 14,
                lineHeight: '22px',
                fontWeight: 500,
                textAlign: 'left',
              }}
            >
              View Insights
            </Link>
          </Box>
        )}
      </Box>
    </Box>
  );
}
